'use client';

import axios from 'axios';
import { RecaptchaV3Action } from 'google-recaptcha/components';
import { useFormWithRecaptchaV3 } from 'google-recaptcha/components';
import { useRouter } from 'next/navigation';
import { type JSX, PropsWithChildren, useCallback, useRef } from 'react';

import { LoginErrorType, useLoginForm } from './LoginFormContext';
import type { CredentialLoginBody, LoginResponse } from './LoginSchemas';

export function LoginForm({
    children,
    redirectAfterLogin,
}: { redirectAfterLogin?: string } & PropsWithChildren): JSX.Element {
    const router = useRouter();
    const { setLoginErrorType } = useLoginForm();
    const formRef = useRef<HTMLFormElement>(null);

    const handleFormSubmit = useCallback(
        async (formData: FormData, recaptchaToken?: string) => {
            const loginFormPayload: CredentialLoginBody = {
                email: formData.get('email') as string,
                password: formData.get('password') as string,
                agreementCheckbox: (formData.get('agreement-checkbox') as string) === 'on',
                recaptchaToken,
                redirectAfterLogin,
            };
            const response = await axios.post<LoginResponse>('/api/login', loginFormPayload);
            if (response.data.success) {
                const redirectTo = response.data.redirectTo ?? '/';
                if (redirectTo) {
                    if (redirectTo.startsWith('http')) {
                        window.location.href = redirectTo;
                    } else {
                        router.push(redirectTo);
                    }
                }
            } else {
                setLoginErrorType(LoginErrorType.INVALID_CREDENTIALS);
            }
        },
        [redirectAfterLogin, router, setLoginErrorType],
    );

    const handleError = useCallback(() => {
        setLoginErrorType(LoginErrorType.INVALID_CREDENTIALS);
    }, [setLoginErrorType]);

    const onSubmit = useFormWithRecaptchaV3({
        action: RecaptchaV3Action.LOGIN_WITH_CREDENTIALS,
        onSubmit: handleFormSubmit,
        onError: handleError,
    });

    return (
        <form ref={formRef} aria-label="Login Form" className="w-full" onSubmit={onSubmit} noValidate>
            {children}
        </form>
    );
}
